/**
 * What the brain autopilot is allowed to do: five numbers, no switches, in the
 * panel the Arendusaju tab's engine strip opens from its gear.
 *
 * Numbers are edited locally and committed on blur, so typing "2" on the way to
 * "20" does not immediately become the new batch size and send a save for a
 * value nobody meant (`prospecting/engines/EngineNumberField.tsx`, shared with
 * the scan autopilot's form). The one boolean - the engine itself - is the
 * switch on the strip, because a stop button with a save step is not a stop
 * button.
 *
 * The hints do real work here. Every number on this form spends either
 * Cloudflare browser time or model tokens, and an operator setting a batch size
 * without being told that ten runs is already more browser time than the free
 * plan gives us in a day is an operator configuring a thing that cannot happen.
 */
import type { BrainAutopilotSettings as Settings } from "../../../../lib/siteBrain";
import { EngineNumberField } from "../../prospecting/engines";
import type { T } from "../../site-scanner/types";

export function BrainAutopilotSettings({
  settings,
  canManage,
  saving,
  onPatch,
  t,
}: {
  settings: Settings;
  canManage: boolean;
  saving: boolean;
  onPatch: (change: Partial<Settings>) => void;
  t: T;
}) {
  const s = settings;
  const locked = !canManage || saving;

  return (
    // Three columns at most, for the reason the scanner's form gives: this is a
    // side panel, not the page's full width.
    <div className="mt-5 space-y-5 border-t border-[var(--crm-hair)] px-[26px] pb-6 pt-5">
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        <EngineNumberField
          label={t("leads.brainAuto.field.interval")}
          hint={t("leads.brainAuto.hint.interval")}
          value={s.intervalMinutes}
          min={30}
          max={1440}
          disabled={locked}
          onCommit={(v) => onPatch({ intervalMinutes: v })}
        />
        <EngineNumberField
          label={t("leads.brainAuto.field.batch")}
          hint={t("leads.brainAuto.hint.batch")}
          value={s.batchSize}
          min={1}
          max={10}
          disabled={locked}
          onCommit={(v) => onPatch({ batchSize: v })}
        />
        <EngineNumberField
          label={t("leads.brainAuto.field.rerun")}
          hint={t("leads.brainAuto.hint.rerun")}
          value={s.rerunAfterDays}
          min={7}
          max={365}
          disabled={locked}
          onCommit={(v) => onPatch({ rerunAfterDays: v })}
        />
        <EngineNumberField
          label={t("leads.brainAuto.field.minScore")}
          hint={t("leads.brainAuto.hint.minScore")}
          value={s.minLeadScore}
          min={0}
          max={100}
          disabled={locked}
          onCommit={(v) => onPatch({ minLeadScore: v })}
        />
        <EngineNumberField
          label={t("leads.brainAuto.field.share")}
          hint={t("leads.brainAuto.hint.share")}
          value={s.budgetSharePct}
          // The ceiling is 80, not 100, and the field enforces it rather than
          // letting the server silently correct it: the operator's own button
          // must always have browser time left.
          min={10}
          max={80}
          disabled={locked}
          onCommit={(v) => onPatch({ budgetSharePct: v })}
        />
      </div>
    </div>
  );
}
